import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Euro, TrendingUp, XCircle, RotateCcw, Search, RefreshCw,
  CheckCircle2, AlertTriangle, Ban, ShoppingCart, Users, CalendarDays } from 'lucide-react';
import { apiFetch } from '../../services/api';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  paid:       { label: 'Bezahlt',       cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', icon: CheckCircle2 },
  active:     { label: 'Aktiv',         cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', icon: CheckCircle2 },
  cancelled:  { label: 'Gekündigt',     cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40',       icon: AlertTriangle },
  chargeback: { label: 'Rücklastschr.', cls: 'bg-rose-500/20 text-rose-300 border-rose-500/40',           icon: Ban },
  refunded:   { label: 'Erstattet',     cls: 'bg-rose-500/20 text-rose-300 border-rose-500/40',           icon: RotateCcw } };

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status || '—', cls: 'bg-gray-700/40 text-gray-300 border-gray-600', icon: ShoppingCart };
  const Icon = meta.icon;
  return (
    <Badge className={`${meta.cls} inline-flex items-center gap-1 border`} data-testid={`sales-badge-${status}`}>
      <Icon className="h-3 w-3" /> {meta.label}
    </Badge>
  );
};

const fmtEuro = (v) => (typeof v === 'number' ? v : parseFloat(v || 0))
  .toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
};

const FILTERS = [
  { key: '', label: 'Alle' },
  { key: 'paid', label: 'Bezahlt' },
  { key: 'cancelled', label: 'Gekündigt' },
  { key: 'chargeback', label: 'Rücklastschrift' },
  { key: 'refunded', label: 'Erstattet' },
];

const SalesOrdersPanel = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total_revenue_visible: 0, active_subscriptions: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (statusFilter) params.set('status', statusFilter);
      const res = await apiFetch(`${API}/api/admin/sales-orders?${params}`, {
        headers: {} });
      const data = await res.json();
      if (data?.success) {
        setOrders(data.orders || []);
        setStats(data.stats || { total_revenue_visible: 0, active_subscriptions: 0 });
      }
    } catch (err) {
      console.error('Sales-Orders Laden fehlgeschlagen:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Zusätzliche client-side Statistik: Cancel + Chargeback + Refund
  const totalCancellations = useMemo(
    () => orders.filter((o) => ['cancelled', 'chargeback', 'refunded'].includes(o.status)).length,
    [orders]
  );

  // Suche über E-Mail und Order-ID (client-side)
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) =>
      (o.email || '').toLowerCase().includes(q) ||
      (o.digistore_order_id || '').toLowerCase().includes(q) ||
      (o.buyer_name || '').toLowerCase().includes(q)
    );
  }, [orders, search]);

  return (
    <div className="space-y-4" data-testid="sales-orders-panel">
      {/* KPI-KARTEN */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 border-emerald-500/30" data-testid="kpi-revenue">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-300 uppercase tracking-wider font-semibold">Total-Umsatz</p>
                <p className="text-3xl font-black text-white mt-1">{fmtEuro(stats.total_revenue_visible)}</p>
                <p className="text-xs text-gray-400 mt-1">aktiv bezahlt / laufend</p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-500/20"><Euro className="h-6 w-6 text-emerald-300" /></div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-sky-500/15 to-sky-500/5 border-sky-500/30" data-testid="kpi-active">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-sky-300 uppercase tracking-wider font-semibold">Aktive Abos</p>
                <p className="text-3xl font-black text-white mt-1">{stats.active_subscriptions}</p>
                <p className="text-xs text-gray-400 mt-1">laufende Verlängerungen</p>
              </div>
              <div className="p-3 rounded-xl bg-sky-500/20"><TrendingUp className="h-6 w-6 text-sky-300" /></div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-rose-500/15 to-rose-500/5 border-rose-500/30" data-testid="kpi-cancel">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-rose-300 uppercase tracking-wider font-semibold">Storn. & Rücklastschr.</p>
                <p className="text-3xl font-black text-white mt-1">{totalCancellations}</p>
                <p className="text-xs text-gray-400 mt-1">alle Zeiträume</p>
              </div>
              <div className="p-3 rounded-xl bg-rose-500/20"><XCircle className="h-6 w-6 text-rose-300" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* FILTER + SUCHE */}
      <Card className="bg-gray-900/60 border-gray-800">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {FILTERS.map((f) => (
                <Button
                  key={f.key || 'all'}
                  size="sm"
                  variant={statusFilter === f.key ? 'default' : 'outline'}
                  onClick={() => setStatusFilter(f.key)}
                  className={statusFilter === f.key
                    ? 'bg-yellow-500 text-black hover:bg-yellow-400 border-yellow-500'
                    : 'border-gray-700 text-gray-300 hover:bg-gray-800'}
                  data-testid={`sales-filter-${f.key || 'all'}`}
                >
                  {f.label}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="E-Mail oder Bestell-ID…"
                  className="pl-9 bg-gray-800 border-gray-700 text-white w-64"
                  data-testid="sales-search"
                />
              </div>
              <Button
                size="sm" variant="outline" onClick={load}
                className="border-gray-700 text-gray-300 hover:bg-gray-800"
                data-testid="sales-reload"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>

        {/* TABELLE */}
        <CardContent>
          {loading && orders.length === 0 ? (
            <p className="text-center py-8 text-gray-500">Lade Bestellungen…</p>
          ) : visible.length === 0 ? (
            <div className="text-center py-10">
              <ShoppingCart className="h-10 w-10 text-gray-600 mx-auto mb-2" />
              <p className="text-gray-500">Keine Bestellungen für die aktuelle Auswahl.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sales-orders-table">
                <thead className="text-xs text-gray-400 uppercase tracking-wider border-b border-gray-800">
                  <tr>
                    <th className="text-left py-2 px-2">Bestell-ID</th>
                    <th className="text-left py-2 px-2">Kunde</th>
                    <th className="text-left py-2 px-2">Produkt</th>
                    <th className="text-right py-2 px-2"><Users className="h-3 w-3 inline mr-1" />Seats</th>
                    <th className="text-right py-2 px-2">Betrag</th>
                    <th className="text-center py-2 px-2">Status</th>
                    <th className="text-left py-2 px-2"><CalendarDays className="h-3 w-3 inline mr-1" />Verlängerung</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {visible.map((o) => (
                    <tr key={o._id || o.digistore_order_id} className="hover:bg-gray-800/30" data-testid={`sales-row-${o.digistore_order_id}`}>
                      <td className="py-2 px-2 text-gray-300 font-mono text-xs">{o.digistore_order_id}</td>
                      <td className="py-2 px-2">
                        <div className="text-white">{o.buyer_name || '—'}</div>
                        <div className="text-xs text-gray-500">{o.email}</div>
                      </td>
                      <td className="py-2 px-2 text-gray-300">
                        {o.product_name || `Produkt ${o.digistore_product_id}`}
                        {o.is_rebill && <Badge className="ml-2 bg-purple-500/15 text-purple-300 border-purple-500/30 text-[10px]">Rebill #{o.rebill_count}</Badge>}
                      </td>
                      <td className="py-2 px-2 text-right text-white font-semibold">{o.quantity || 1}</td>
                      <td className="py-2 px-2 text-right text-emerald-300 font-semibold">{fmtEuro(o.amount)}</td>
                      <td className="py-2 px-2 text-center"><StatusBadge status={o.status} /></td>
                      <td className="py-2 px-2 text-gray-400">{fmtDate(o.renewsAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-gray-500 text-right mt-2">
                {visible.length} {visible.length === 1 ? 'Bestellung' : 'Bestellungen'}
                {search && ` · Filter: "${search}"`}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SalesOrdersPanel;
